/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Tickler — tracks completion of each step in `processStory` so a partial
 * failure can be resumed by re-running with the same tickler instance. Each
 * step carries a different `data` shape; the `StepDataMap` typed-key index
 * exposes that shape end-to-end so `tickler.getStepData('encryptStory').nonce`
 * is typed `Uint8Array` (caught the class of bug behind #147).
 */

import type { SignedContent } from './vault.js';
import type { RoutingSheet } from './document/routingsheet.js';

/** Verified F12 record consumed by F14's routing-sheet builder. */
export interface VerifiedKeyRecord {
  /** ML-KEM-768 public key (1184 bytes). */
  kemPublicKey: Uint8Array;
  /** SHA-256 of the signing public key — the F12 TOFU pin. */
  signingKeyFingerprint: Uint8Array;
}

/**
 * The shape of the `publicKeys` payload returned by `requestPublicKeys` and
 * stored in the tickler's `requestPublicKeys` step. Note: the
 * `requestPublicKeys` helper returns `{publicKeys, contents}` but only
 * `publicKeys` is persisted on the tickler — `contents` is threaded into
 * `vaultService.updateContents()` at the call site.
 */
export interface PublicKeysBundle {
  author: VerifiedKeyRecord;
  system: VerifiedKeyRecord;
  participants: Record<string, VerifiedKeyRecord>;
  authorId: string;
}

/** Encrypted-story payload persisted by step 3 (`encryptStory`). */
export interface EncryptedStoryStepData {
  nonce: Uint8Array;
  ciphertext: Uint8Array;
  documentId: string;
  version: number;
}

/** DK generated by step 1 (`generateSymmetricKey`). */
export interface DocumentKey {
  id: string;
  key: Uint8Array;
}

/** Server response from `POST /api/story` (step 8). */
export interface SavedStoryResult {
  id: string;
  checksum?: string;
  [key: string]: unknown;
}

/** Server response from `POST /api/disclosure` (step 9). */
export interface SavedDisclosureResult {
  id: string;
  [key: string]: unknown;
}

/**
 * Per-step `data` types. The keys are the canonical step names; the values
 * are exactly what `markStepCompleted(name, data)` stores and what
 * `getStepData(name)` returns.
 */
export interface StepDataMap {
  generateSymmetricKey: DocumentKey;
  saveSymmetricKey: null;
  encryptStory: EncryptedStoryStepData;
  requestPublicKeys: PublicKeysBundle;
  signStory: SignedContent;
  encryptSymmetricKeys: RoutingSheet;
  saveDisclosureLocal: { disclosureId: string };
  saveStoryServer: SavedStoryResult;
  saveDisclosureServer: SavedDisclosureResult;
  cleanup: null;
}

/** Union of all step names supported by the pipeline. */
export type StepName = keyof StepDataMap;

export type StepStatus =
  'pending' | 'completed' | 'failed' | 'in_progress' | 'unknown';

/**
 * Internal record stored per step. `data` is typed `unknown` at the storage
 * layer (heterogeneous map keyed by step name) and narrowed at the public
 * API surface via `StepDataMap[K]`.
 */
interface StepRecord {
  status: Exclude<StepStatus, 'unknown'>;
  data: unknown;
  error: Error | null;
}

const STEP_NAMES: readonly StepName[] = [
  'generateSymmetricKey',
  'saveSymmetricKey',
  'encryptStory',
  'requestPublicKeys',
  'signStory',
  'encryptSymmetricKeys',
  'saveDisclosureLocal',
  'saveStoryServer',
  'saveDisclosureServer',
  'cleanup',
];

const initialStep = (): StepRecord => ({
  status: 'pending',
  data: null,
  error: null,
});

const initialSteps = (): Record<StepName, StepRecord> => {
  const steps = {} as Record<StepName, StepRecord>;
  for (const name of STEP_NAMES) steps[name] = initialStep();
  return steps;
};

export interface TicklerStatusSnapshot {
  completed: StepName[];
  failed: StepName[];
  pending: StepName[];
  canResume: boolean;
}

export class StoryProcessingTickler {
  steps: Record<StepName, StepRecord>;

  constructor() {
    this.steps = initialSteps();
  }

  markStepCompleted<K extends StepName>(
    stepName: K,
    data: StepDataMap[K] | null = null
  ): void {
    if (this.steps[stepName]) {
      this.steps[stepName] = { status: 'completed', data, error: null };
    }
  }

  markStepFailed(stepName: StepName, error: Error): void {
    if (this.steps[stepName]) {
      this.steps[stepName] = { status: 'failed', data: null, error };
    }
  }

  markStepInProgress(stepName: StepName): void {
    if (this.steps[stepName]) {
      this.steps[stepName] = {
        ...this.steps[stepName],
        status: 'in_progress',
        error: null,
      };
    }
  }

  getStepStatus(stepName: StepName): StepStatus {
    return this.steps[stepName]?.status ?? 'unknown';
  }

  getStepData<K extends StepName>(stepName: K): StepDataMap[K] | undefined {
    const record = this.steps[stepName];
    if (!record || record.data === null) return undefined;
    return record.data as StepDataMap[K];
  }

  getStepError(stepName: StepName): Error | undefined {
    return this.steps[stepName]?.error ?? undefined;
  }

  getCompletedSteps(): StepName[] {
    return STEP_NAMES.filter(step => this.steps[step].status === 'completed');
  }

  getFailedSteps(): StepName[] {
    return STEP_NAMES.filter(step => this.steps[step].status === 'failed');
  }

  getPendingSteps(): StepName[] {
    return STEP_NAMES.filter(step => this.steps[step].status === 'pending');
  }

  canResume(): boolean {
    return (
      this.getFailedSteps().length > 0 || this.getPendingSteps().length > 0
    );
  }

  getStatus(): TicklerStatusSnapshot {
    return {
      completed: this.getCompletedSteps(),
      failed: this.getFailedSteps(),
      pending: this.getPendingSteps(),
      canResume: this.canResume(),
    };
  }
}

export const createStoryProcessingTickler = (): StoryProcessingTickler =>
  new StoryProcessingTickler();
